// 画面B：ドライバー承認（スマホ・モバイルファースト）
// デザイン正本: design_handoff（Claude Design）の screenB.jsx を React+TS に忠実移植。
// ドライバーが配布URLをスマホで開き、自分の月次明細を確認 → 承認/質問する画面。
// MoneyForward 関連は一切含めない（出口は自前PDF）。

import React, { useEffect, useState } from "react";
import "../styles/driver-approval.css";
import logo from "../assets/hsd-logo.png";
import type { Statement, Carrier } from "../types";
import { noticeRecipient, displayInvoiceNo } from "../lib/calc/recipient";

// ===== フォーマット・集計（design: shared/format.js 相当） =====
const group = (n: number) => Math.round(Math.abs(n)).toLocaleString("ja-JP");

type SalesRow = {
  id: string; date?: string; wd?: string; isSun?: boolean;
  kind: string; qty: number; unit: number; unitName?: string;
  formula?: string; qtyLabel?: string; warn?: boolean;
  detail?: string; // 品目詳細（調整・臨時売上から伝播。表示専用）
  reduced?: boolean; // 軽減税率(8%)対象。通知書で品目に ※ を付ける
};
// detail … 臨時控除の備考（本城さん要望 2026-09-07）。控除名の下に小さく出す
type Deduction = { id: string; label: string; note?: string; detail?: string; amount: number; reduced?: boolean };
// 承認画面の表示モデル（ダミープレビューと実Statementの両方がこの形に変換される）
export type ApprovalView = {
  id: string; name: string; mgmtNo: string; affiliation: string;
  org?: string; // マスターB列「所属」(委託元会社名等)。通知書の宛名に使う（#30）
  invoiceRegistered: boolean; invoiceNo?: string | null; month: string; payDate: string; bank: string;
  salesType: "daily" | "shunan"; rows: SalesRow[]; deductions: Deduction[];
  invoiceDeductionRate: number;
  // 税集計（本城さん指示 6/19。Statement.taxSummary から転記。旧スナップショットは 0 → 税行を出さない）
  taxable10: number;
  tax10: number;
  // 軽減税率8%（本城さん要望 2026-08-07）。対象が無ければ 0/undefined で従来表示のまま
  taxable8?: number;
  tax8?: number;
};
type Driver = ApprovalView;

const company = {
  name: "株式会社HS-deliverys",
  zip: "〒753-0056",
  addr: "山口県山口市湯田温泉3丁目7番5号",
  tel: "083-920-5210",
  invoiceNo: "T7120001236475",
};

const carrierLabel = (c?: Carrier): string => (c === "yamato" ? "ヤマト" : c === "sagawa" ? "佐川" : "");

// 実Statement → 承認画面の表示モデルへ変換（公開承認ページが使う）
export function statementToApprovalView(stmt: Statement): ApprovalView {
  const isShunan = !!stmt.specialRule && stmt.specialRule.includes("周南");
  const multi = (stmt.carriers?.length ?? 1) > 1;
  const sales = stmt.items.filter((i) => i.category === "sales");

  let rows: SalesRow[];
  if (isShunan) {
    rows = sales.map((it, i) => ({
      id: `s${i}`,
      kind: it.name,
      qty: it.quantity,
      unit: it.unitPrice ?? 0,
      qtyLabel: group(it.quantity),
      detail: it.detail,
    }));
  } else {
    // 日付順にソート（UI版 dailyItems と同じ並び）。元の stmt.items は便種グループ順なので、
    // ここでソートしないと PrintSheet で日付なしのネコポス/その他が末尾に溜まる。
    // 安定ソートなので同一日内は元の便種順（宅急便→ネコポス→その他）を保つ。
    const sorted = sales.slice().sort((a, b) =>
      a.deliveryDate < b.deliveryDate ? -1 : a.deliveryDate > b.deliveryDate ? 1 : 0
    );
    rows = sorted.map((it, i) => {
      const dt = new Date(it.deliveryDate + "T00:00:00");
      const wd = isNaN(dt.getTime()) ? 0 : dt.getDay();
      const [, mm, dd] = it.deliveryDate.split("-");
      const kind = multi && it.carrier ? `【${carrierLabel(it.carrier)}】${it.name}` : it.name;
      return {
        id: `s${i}`,
        date: `${Number(mm)}/${Number(dd)}`,
        wd: WD[wd],
        isSun: wd === 0,
        kind,
        qty: it.quantity,
        unit: it.unitPrice ?? 0,
        unitName: "個",
        detail: it.detail,
        reduced: it.taxRate === "8%",
      };
    });
  }

  // 控除：自動控除(deduct) + 手入力(manualで単価入力済み)。未入力の手入力枠はスキップ。
  const deductions: Deduction[] = [];
  stmt.items.forEach((it, i) => {
    if (it.category === "deduct") {
      deductions.push({
        id: `d${i}`,
        label: it.name,
        // 日割りの根拠（例「日割り 27/30日（2026-06-04 入社）」）はドライバー本人にも見せる。
        // それ以外の note は社内向け（マスター由来の説明・識別マーカー）なので出さない。
        note: it.note === "__extra__" ? "臨時" : it.note?.startsWith("日割り") ? it.note : undefined,
        detail: it.note === "__extra__" ? it.detail : undefined,
        amount: Math.abs(it.amount),
        reduced: it.taxRate === "8%",
      });
    } else if (it.category === "manual" && it.unitPrice != null) {
      deductions.push({ id: `d${i}`, label: it.name, amount: Math.abs(it.amount), reduced: it.taxRate === "8%" });
    }
  });

  const [y, m] = stmt.targetMonth.split("-");
  const carriers = stmt.carriers ?? [stmt.carrier];
  return {
    id: stmt.driverId,
    name: stmt.driverName,
    mgmtNo: stmt.driverId,
    affiliation: [stmt.branch, ...carriers.map(carrierLabel)].filter(Boolean).join(" / "),
    org: stmt.org,
    invoiceRegistered: !!stmt.invoiceNo,
    invoiceNo: stmt.invoiceNo ?? null, // 通知書に「登録番号：T…」を出すため（本城さん要望 2026-08-07）
    month: `${y}年${Number(m)}月度`,
    payDate: stmt.dueDate || stmt.billingDate || "",
    bank: "",
    salesType: isShunan ? "shunan" : "daily",
    rows,
    deductions,
    invoiceDeductionRate: 0, // 値引きは items に控除行として入っているため、ここでは常に0
    taxable10: stmt.taxSummary?.taxable10 ?? 0,
    tax10: stmt.taxSummary?.tax10 ?? 0,
    taxable8: stmt.taxSummary?.taxable8 ?? 0,
    tax8: stmt.taxSummary?.tax8 ?? 0,
  };
}

// 2026年5月（5/1=金）。weekday: 0=日,1=月,...6=土
const WD = ["日", "月", "火", "水", "木", "金", "土"];
const weekdayOf = (day: number) => (5 + (day - 1)) % 7;

function buildYamatoRows(): SalesRow[] {
  const yamatoUnit: Record<string, number> = {
    "宅配": 153, "メール便": 75, "その他": 280,
  };
  const rows: SalesRow[] = [];
  let id = 1;
  for (let d = 1; d <= 31; d++) {
    const wd = weekdayOf(d);
    const isSun = wd === 0;
    let qa = 0, qb = 0, qc = 0;
    if (!isSun) {
      qa = 78 + ((d * 13 + 5) % 55);
      qb = 16 + ((d * 11 + 3) % 30);
      qc = d % 9 === 0 ? 1 + (d % 3) : 0;
    }
    const dateStr = `5/${d}`;
    rows.push({ id: `r${id++}`, date: dateStr, wd: WD[wd], isSun, kind: "宅配", qty: qa, unit: yamatoUnit["宅配"], unitName: "個" });
    rows.push({ id: `r${id++}`, date: dateStr, wd: WD[wd], isSun, kind: "メール便", qty: qb, unit: yamatoUnit["メール便"], unitName: "個" });
    rows.push({ id: `r${id++}`, date: dateStr, wd: WD[wd], isSun, kind: "その他", qty: qc, unit: yamatoUnit["その他"], unitName: "個" });
  }
  return rows;
}

// 主役: 通常ヤマトドライバー 田中 健一（日別・0個表示が映える）
const tanaka: Driver = {
  id: "tanaka", name: "田中 健一", mgmtNo: "HSD-0427",
  affiliation: "ヤマト運輸 徳山主管支店", invoiceRegistered: false,
  month: "2026年5月度", payDate: "2026/06/15",
  bank: "山口銀行 徳山支店 / 普通 1234567",
  salesType: "daily", rows: buildYamatoRows(),
  deductions: [
    { id: "d1", label: "携帯リース", note: "月額", amount: 5000 },
    { id: "d2", label: "車両リース", note: "月額", amount: 35000 },
    { id: "d3", label: "ガソリンカード", note: "当月利用分", amount: 28430 },
    { id: "d4", label: "アプリ料", note: "月額", amount: 3300 },
    { id: "d5", label: "指導料", note: "2件 × 1,000円", amount: 2000 },
    { id: "d6", label: "臨時控除（ETC代）", note: "4/分 立替", amount: 4200 },
  ],
  invoiceDeductionRate: 0.02, // 未登録 → 売上×2%（消費税相当の一部）
  taxable10: 0, // プレビュー用ダミーは税行なし（実データは statementToApprovalView が設定）
  tax10: 0,
};

function totals(d: Driver) {
  let sales = 0;
  d.rows.forEach((r) => { sales += (r.qty || 0) * (r.unit || 0); });
  const invoiceDed = d.invoiceDeductionRate > 0 ? Math.round(sales * d.invoiceDeductionRate) : 0;
  let ded = 0;
  d.deductions.forEach((x) => { ded += x.amount || 0; });
  ded += invoiceDed;
  const tax10 = d.tax10 || 0;
  const tax8 = d.tax8 || 0;
  // 差引支給額は税込（小計 + 消費税）。旧スナップショット（tax10=0）は従来どおり税抜
  return { sales, invoiceDed, ded, tax10, tax8, taxTotal: tax10 + tax8, net: sales - ded + tax10 + tax8 };
}

function salesByKind(d: Driver): { name: string; qty: string; amount: number; detail?: string; reduced?: boolean }[] {
  if (d.salesType === "shunan") {
    return d.rows.map((r) => ({ name: r.kind, qty: r.qtyLabel || "", amount: (r.qty || 0) * (r.unit || 0), detail: r.detail, reduced: r.reduced }));
  }
  // detail もキーに含める（品目詳細が異なる調整行は別の明細行として通知書に出す）
  // 税率（軽減8%かどうか）もキーに含める＝適格請求書は税率ごとに区分して記載する
  const map: Record<string, { name: string; qty: number; amount: number; detail?: string; reduced?: boolean }> = {};
  const order: string[] = [];
  d.rows.forEach((r) => {
    const key = `${r.kind}__${r.detail ?? ""}__${r.reduced ? "8" : "10"}`;
    if (!map[key]) { map[key] = { name: r.kind, qty: 0, amount: 0, detail: r.detail, reduced: r.reduced }; order.push(key); }
    map[key].qty += r.qty;
    map[key].amount += (r.qty || 0) * (r.unit || 0);
  });
  return order.map((k) => ({ name: map[k].name, qty: group(map[k].qty) + " 個", amount: map[k].amount, detail: map[k].detail, reduced: map[k].reduced }));
}

// 承認した瞬間に画面へ出す日時。デザイン移植時は日付が固定値で残っていて、
// 承認直後だけ「2026/06/08 に承認」と出ていた（リロードするとサーバ由来の正しい日時に直る）。
function nowStamp() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// 売上明細表の対象期間。month は "2026年7月度" 形式（statementToApprovalView が作る）。
// 読めない形式のときは期間を出さない（誤った期間を出すより無いほうが安全）。
export function periodLabel(month: string): string {
  const m = month.match(/^(\d{4})年(\d{1,2})月度$/);
  if (!m) return `対象期間：${month}`;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const lastDay = new Date(y, mo, 0).getDate(); // mo 月の最終日（28〜31）
  return `対象期間：${y}年${mo}月1日 〜 ${mo}月${lastDay}日（${month}）`;
}

// ===== アイコン（design: shared/ui.jsx 相当） =====
type IconName = "warn" | "chevron-down" | "doc" | "check" | "download" | "lock";
function Icon({ name, size = 16, color = "currentColor", stroke = 1.7 }: { name: IconName; size?: number; color?: string; stroke?: number }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: stroke, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (name) {
    case "chevron-down": return <svg {...p}><path d="M6 9l6 6 6-6" /></svg>;
    case "check": return <svg {...p}><path d="M5 12.5l4.5 4.5L19 7" /></svg>;
    case "download": return <svg {...p}><path d="M12 4v11M7 11l5 5 5-5M5 20h14" /></svg>;
    case "warn": return <svg {...p}><path d="M12 3l9.5 16.5h-19z" /><path d="M12 10v4" /><circle cx="12" cy="17.2" r="0.6" fill={color} stroke="none" /></svg>;
    case "doc": return <svg {...p}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /></svg>;
    case "lock": return <svg {...p}><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></svg>;
    default: return null;
  }
}

const sbHead: React.CSSProperties = { fontSize: 12.5, fontWeight: 800, color: "#4d5765", letterSpacing: ".04em", margin: "0 0 6px" };
const sbRow: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", fontSize: 14, borderBottom: "1px solid #f0f2f6" };
const primaryBtn: React.CSSProperties = { width: "100%", height: 54, borderRadius: 14, background: "var(--blue)", color: "#fff", border: "none", fontFamily: "inherit", fontSize: 16.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: "pointer", boxShadow: "0 6px 16px rgba(37,99,235,.28)" };
const ghostBtn: React.CSSProperties = { width: "100%", height: 50, borderRadius: 14, background: "#fff", color: "#4d5765", border: "1.5px solid var(--border-strong)", fontFamily: "inherit", fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" };

// 折りたたみ内訳
function Breakdown({ d, t, sales }: { d: Driver; t: ReturnType<typeof totals>; sales: ReturnType<typeof salesByKind> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={sbHead}>売上の内訳</div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {sales.map((s, i) => (
            <div key={i} style={sbRow}>
              <span style={{ color: "#1b2536", fontWeight: 600 }}>
                {s.name}
                {s.detail && <div style={{ fontWeight: 400, color: "#828b99", fontSize: 11.5, marginTop: 2 }}>{s.detail}</div>}
              </span>
              <span style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <span className="num" style={{ color: "#828b99", fontSize: 13 }}>{s.qty}</span>
                <span className="num" style={{ fontWeight: 700, minWidth: 78, textAlign: "right" }}>¥{group(s.amount)}</span>
              </span>
            </div>
          ))}
          <div style={{ ...sbRow, borderTop: "1.5px solid #e2e6ec", fontWeight: 800 }}>
            <span>売上合計</span><span className="num">¥{group(t.sales)}</span>
          </div>
        </div>
      </div>
      <div>
        <div style={sbHead}>控除の内訳</div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {d.deductions.map((x) => (
            <div key={x.id} style={sbRow}>
              <span style={{ color: "#1b2536", fontWeight: 600 }}>
                {x.label}
                {x.detail && <div style={{ fontWeight: 400, color: "#828b99", fontSize: 11.5, marginTop: 2 }}>{x.detail}</div>}
              </span>
              <span className="num neg" style={{ fontWeight: 700 }}>△{group(x.amount)}</span>
            </div>
          ))}
          {t.invoiceDed > 0 && (
            <div style={{ ...sbRow, alignItems: "flex-start", flexDirection: "column", gap: 4, background: "#fffaf0", margin: "0 -10px", padding: "10px", borderRadius: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <span style={{ color: "#1b2536", fontWeight: 600 }}>値引き</span>
                <span className="num neg" style={{ fontWeight: 700 }}>△{group(t.invoiceDed)}</span>
              </div>
              <div style={{ fontSize: 12, color: "#8a6d12", lineHeight: 1.5 }}>
                インボイス未登録のため、消費税相当の一部を調整しています。
              </div>
            </div>
          )}
          <div style={{ ...sbRow, borderTop: "1.5px solid #e2e6ec", fontWeight: 800 }}>
            <span>控除合計</span><span className="num neg">△{group(t.ded)}</span>
          </div>
        </div>
      </div>
      {t.tax10 > 0 && (
        <div>
          <div style={sbHead}>合計（税込）</div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={sbRow}>
              <span style={{ color: "#1b2536", fontWeight: 600 }}>小計（売上 − 控除・税抜）</span>
              <span className="num" style={{ fontWeight: 700 }}>¥{group(t.sales - t.ded)}</span>
            </div>
            <div style={sbRow}>
              <span style={{ color: "#1b2536", fontWeight: 600 }}>消費税額合計{t.tax8 !== 0 ? "（10%・8%）" : "（10%）"}</span>
              <span className="num" style={{ fontWeight: 700 }}>¥{group(t.taxTotal)}</span>
            </div>
            <div style={{ ...sbRow, borderTop: "1.5px solid #e2e6ec", fontWeight: 800 }}>
              <span>差引支給額（税込）</span><span className="num">¥{group(t.net)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 承認画面本体
// onApprove/onQuestion を渡すと実サーバ送信モード（公開ページ）。未指定はプレビュー（ローカルのみ）。
function ApprovalApp({
  d,
  onApprove,
  onQuestion,
  onPrint,
  initialApproved,
  approvedStamp,
}: {
  d: Driver;
  onApprove?: () => Promise<boolean>;
  onQuestion?: (q: string) => Promise<boolean>;
  onPrint?: (mode: "notice" | "sales") => void;
  initialApproved?: boolean;
  approvedStamp?: string;
}) {
  const print = onPrint ?? (() => window.print());
  const t = totals(d);
  const sales = salesByKind(d);
  const [open, setOpen] = useState(false);
  const [approved, setApproved] = useState(!!initialApproved);
  const [stamp, setStamp] = useState(approvedStamp || "");
  const [showQ, setShowQ] = useState(false);
  const [q, setQ] = useState("");
  const [qSent, setQSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const approve = async () => {
    setErr(null);
    if (onApprove) {
      setBusy(true);
      const ok = await onApprove().catch(() => false);
      setBusy(false);
      if (!ok) { setErr("送信に失敗しました。電波の良い場所で再度お試しください。"); return; }
    }
    setApproved(true);
    setStamp(nowStamp());
  };
  const sendQuestion = async () => {
    if (!q.trim()) return;
    setErr(null);
    if (onQuestion) {
      setBusy(true);
      const ok = await onQuestion(q).catch(() => false);
      setBusy(false);
      if (!ok) { setErr("送信に失敗しました。再度お試しください。"); return; }
    }
    setQSent(true);
  };

  return (
    <div style={{ minHeight: "100%", background: "#eef0f4", paddingTop: 18, paddingBottom: 40, fontFamily: "'Noto Sans JP', -apple-system, system-ui, sans-serif", color: "#1b2536" }}>
      {/* ブランド帯 */}
      <div style={{ padding: "6px 20px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <img src={logo} alt="HS-deliverys" style={{ height: 22, display: "block" }} />
        <span style={{ fontSize: 12, color: "#828b99", fontWeight: 700, whiteSpace: "nowrap" }}>支払通知</span>
      </div>

      <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        {/* あいさつ */}
        <div>
          <div style={{ fontSize: 14, color: "#5b6573", fontWeight: 600 }}>{d.month}</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{d.name} さん</div>
        </div>

        {/* 確認のお願い（承認期限の日付は出さない — 本城さん指示 2026-08-10。文言はMF請求書と同一） */}
        {!approved && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 9, background: "#fff5d1", border: "1px solid #ecd58a", borderRadius: 12, padding: "11px 14px" }}>
            <span style={{ color: "#8a6d12", flexShrink: 0, display: "inline-flex", marginTop: 2 }}><Icon name="warn" size={15} /></span>
            <div style={{ fontSize: 12, color: "#8a6d12", lineHeight: 1.6 }}>送付後一週間以内に誤りのある旨の連絡がない場合には、記載内容のとおり確認があったものといたします。</div>
          </div>
        )}

        {/* 金額ヒーロー */}
        <div style={{ background: "var(--navy)", color: "#fff", borderRadius: 18, padding: "20px 20px 18px", boxShadow: "0 8px 24px rgba(31,42,68,.22)" }}>
          <div style={{ fontSize: 13, color: "#aeb8cf", fontWeight: 700, letterSpacing: ".04em" }}>差引支給額（お振込額）</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 4 }}>
            <span style={{ fontSize: 24, fontWeight: 700 }}>¥</span>
            <span className="num" style={{ fontSize: 46, fontWeight: 800, lineHeight: 1, letterSpacing: "-.01em" }}>{group(t.net)}</span>
          </div>
          <div style={{ display: "flex", marginTop: 16, borderTop: "1px solid #36456a", paddingTop: 13 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#9fabc6" }}>売上合計</div>
              <div className="num" style={{ fontSize: 15, fontWeight: 700 }}>¥{group(t.sales)}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#9fabc6" }}>控除合計</div>
              <div className="num" style={{ fontSize: 15, fontWeight: 700, color: "#ffb3ad" }}>△{group(t.ded)}</div>
            </div>
            {t.tax10 > 0 && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "#9fabc6" }}>消費税(10%)</div>
                <div className="num" style={{ fontSize: 15, fontWeight: 700 }}>¥{group(t.tax10)}</div>
              </div>
            )}
          </div>
          <div style={{ marginTop: 14, background: "rgba(255,255,255,.08)", borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
            <span style={{ color: "#aeb8cf" }}>振込予定 <b style={{ color: "#fff" }}>{d.payDate}</b></span>
            <span style={{ color: "#aeb8cf" }}>{d.bank.split(" / ")[0]}</span>
          </div>
        </div>

        {/* 内訳を見る */}
        <div className="card" style={{ overflow: "hidden" }}>
          <button onClick={() => setOpen(!open)} style={{ width: "100%", background: "none", border: "none", padding: "15px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontFamily: "inherit" }}>
            <span style={{ fontWeight: 800, fontSize: 15 }}>内訳を見る</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#828b99", fontSize: 12.5 }}>
              便種別・控除別
              <span style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", display: "inline-flex" }}><Icon name="chevron-down" size={18} /></span>
            </span>
          </button>
          {open && (
            <div style={{ padding: "4px 16px 18px", borderTop: "1px solid var(--border)" }}>
              <Breakdown d={d} t={t} sales={sales} />
            </div>
          )}
        </div>

        {/* PDF出力（支払通知書・売上明細表をそれぞれ） */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: -2 }}>
          <button onClick={() => print("notice")} style={{ ...ghostBtn, height: 46, fontSize: 14, borderColor: "var(--blue-border)", color: "var(--blue-dark)" }}>
            <Icon name="download" size={17} /> 支払通知書（PDF）をダウンロード
          </button>
          <button onClick={() => print("sales")} style={{ ...ghostBtn, height: 46, fontSize: 14 }}>
            <Icon name="doc" size={17} /> 売上明細表（PDF）をダウンロード
          </button>
        </div>

        {/* アクション / 承認後 */}
        {err && (
          <div style={{ background: "#fdecea", border: "1px solid #f5b5b0", borderRadius: 10, padding: "10px 12px", color: "#b3261e", fontSize: 13 }}>
            {err}
          </div>
        )}
        {!approved ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 2 }}>
            <button onClick={approve} disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>
              <Icon name="check" size={20} /> {busy ? "送信中..." : "この内容で承認する"}
            </button>
            {!showQ ? (
              <button onClick={() => setShowQ(true)} style={ghostBtn}>質問・相違がある</button>
            ) : (
              <div className="card" style={{ padding: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>気になる点を事務所に伝える</div>
                {qSent ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--green)", fontSize: 13.5, fontWeight: 700, padding: "6px 0" }}>
                    <Icon name="check" size={18} /> 送信しました。事務所から折り返します。
                  </div>
                ) : (
                  <>
                    <textarea value={q} onChange={(e) => setQ(e.target.value)} placeholder="例）ガソリンカードの金額を確認したいです" style={{ width: "100%", minHeight: 76, resize: "none", border: "1px solid var(--border-strong)", borderRadius: 10, padding: "10px 12px", fontFamily: "inherit", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
                    <button onClick={sendQuestion} disabled={!q.trim() || busy} style={{ ...primaryBtn, height: 44, marginTop: 8, opacity: q.trim() && !busy ? 1 : 0.5 }}>{busy ? "送信中..." : "送信する"}</button>
                  </>
                )}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: "#828b99", fontSize: 11.5, marginTop: 2 }}>
              <Icon name="lock" size={13} /> 閲覧・承認の状況は事務所に自動で通知されます
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 2 }}>
            <div style={{ background: "var(--green-soft)", border: "1px solid var(--green-border)", borderRadius: 14, padding: "16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "var(--green)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
                <Icon name="check" size={22} stroke={2.4} />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: "var(--green)" }}>承認済み</div>
                <div style={{ fontSize: 12, color: "#4d5765", marginTop: 1 }} className="num">{stamp} に承認</div>
              </div>
            </div>
            <div style={{ textAlign: "center", color: "#828b99", fontSize: 11.5 }}>
              事務所に「承認済み」が通知されました。お振込は {d.payDate} 予定です。
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 6, fontSize: 11.5, color: "#a7aebb" }}>
          このページはあなた専用のリンクです（他の人は閲覧できません）
        </div>
      </div>
    </div>
  );
}

// 印刷用：売上明細表（A4・日別/便種別 全行）
function PrintSheet({ d }: { d: Driver }) {
  const t = totals(d);
  const shunan = d.salesType === "shunan";
  const th: React.CSSProperties = { padding: "5px 8px", fontSize: 10.5, fontWeight: 700, borderBottom: "1.5px solid #1b2536", textAlign: "left" };
  const thr: React.CSSProperties = { ...th, textAlign: "right" };
  const td: React.CSSProperties = { padding: "4px 8px", fontSize: 10.5, borderBottom: "1px solid #e8ebf0" };
  const tdr: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  return (
    <div style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#1b2536" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1b2536", paddingBottom: 8 }}>
        <div>
          <img src={logo} alt="HS-deliverys" style={{ height: 19, display: "block", marginBottom: 7 }} />
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: ".08em" }}>売上明細表</div>
          <div style={{ fontSize: 11, color: "#4d5765", marginTop: 4 }}>{periodLabel(d.month)}</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 10.5, color: "#4d5765" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#1b2536" }}>{company.name}</div>
          <div>{company.zip} {company.addr}</div>
          <div>登録番号 {company.invoiceNo}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 24, margin: "10px 0 12px", fontSize: 11 }}>
        <div><span style={{ color: "#828b99" }}>氏名</span>　<b style={{ fontSize: 13 }}>{d.name}　様</b></div>
        <div><span style={{ color: "#828b99" }}>管理番号</span>　{d.mgmtNo}</div>
        <div><span style={{ color: "#828b99" }}>所属</span>　{d.affiliation}</div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          {shunan
            ? <><th style={th}>区分</th><th style={th}>算定方法</th><th style={thr}>実績</th><th style={thr}>単価(円)</th><th style={thr}>金額(円)</th></>
            : <><th style={{ ...th, width: 70 }}>日付</th><th style={th}>便種</th><th style={thr}>実績(個)</th><th style={thr}>単価(円)</th><th style={thr}>金額(円)</th></>}
        </tr></thead>
        <tbody>
          {d.rows.map((r, i) => {
            const amt = (r.qty || 0) * (r.unit || 0);
            if (shunan) {
              return <tr key={r.id}><td style={td}>{r.kind}{r.detail && <div style={{ fontSize: 9, color: "#828b99", marginTop: 1 }}>{r.detail}</div>}</td><td style={{ ...td, color: "#828b99", fontSize: 9.5 }}>{r.formula}</td><td style={tdr}>{r.qtyLabel}</td><td style={tdr}>{group(r.unit)}</td><td style={{ ...tdr, fontWeight: 700 }}>{group(amt)}</td></tr>;
            }
            // 日付が変わった行にだけ日付を出す（rows は日付順ソート済み＝UI版 SalesPrintSheet と同方式）。
            // 旧実装は「宅急便の行だけ日付」で、ネコポス/その他が日付なしで末尾に溜まる不具合があった。
            const prev = d.rows[i - 1];
            const showDate = !prev || prev.date !== r.date;
            const zero = (r.qty || 0) === 0;
            return (
              <tr key={r.id} style={showDate ? { borderTop: "1px solid #cdd3dc" } : undefined}>
                <td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap", color: r.isSun ? "#cc2f2a" : "#1b2536" }}>{showDate ? `${r.date}(${r.wd})` : ""}</td>
                <td style={{ ...td, color: zero ? "#a7aebb" : "#1b2536" }}>
                  {r.kind}
                  {r.detail && <div style={{ fontSize: 9, color: "#828b99", marginTop: 1 }}>{r.detail}</div>}
                </td>
                <td style={{ ...tdr, color: zero ? "#a7aebb" : "#1b2536" }}>{group(r.qty)} 個</td>
                <td style={{ ...tdr, color: zero ? "#a7aebb" : "#1b2536" }}>{group(r.unit)}</td>
                <td style={{ ...tdr, fontWeight: zero ? 400 : 700, color: zero ? "#a7aebb" : "#1b2536" }}>{group(amt)}</td>
              </tr>
            );
          })}
          <tr><td style={{ ...td, borderTop: "1.5px solid #1b2536", borderBottom: "none", fontWeight: 800 }} colSpan={4}>売上合計</td><td style={{ ...tdr, borderTop: "1.5px solid #1b2536", borderBottom: "none", fontWeight: 800, fontSize: 13 }}>¥{group(t.sales)}</td></tr>
        </tbody>
      </table>

      <div style={{ marginTop: 14, fontSize: 9.5, color: "#828b99", lineHeight: 1.6 }}>
        ※ 本表は売上（配達実績）の明細です。控除・差引支給額は「支払通知書」をご確認ください。{!shunan && "数量0の日も記録として表示しています。"}<br />
        発行：{company.name}　／　適格請求書等保存方式（インボイス制度）対応
      </div>
    </div>
  );
}

// 印刷用：支払通知書（A4）。売上・控除・差引支給を1枚にまとめた正式通知書。
function NoticeSheet({ d }: { d: Driver }) {
  const t = totals(d);
  const sales = salesByKind(d);
  const ntd: React.CSSProperties = { padding: "4px 8px", fontSize: 10.5, borderBottom: "1px solid #eef1f5" };
  const nth: React.CSSProperties = { padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #cdd3dc", fontWeight: 700, fontSize: 10, background: "#f4f6f9" };
  return (
    <div style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#1b2536", fontSize: 11, lineHeight: 1.5 }}>
      <div style={{ textAlign: "center", fontSize: 17, fontWeight: 800, letterSpacing: ".3em", borderBottom: "2px solid #1f2a44", paddingBottom: 8 }}>支　払　通　知　書</div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, alignItems: "flex-start" }}>
        <div>
          {(() => {
            const rcp = noticeRecipient(d.org, d.name);
            return (
              <>
                <div style={{ fontSize: 15, fontWeight: 800, borderBottom: "1px solid #1b2536", paddingBottom: 2, display: "inline-block" }}>{rcp.name}　{rcp.honorific}</div>
                <div style={{ marginTop: 8, color: "#4d5765" }}>管理番号：{d.mgmtNo}</div>
                {displayInvoiceNo(d.invoiceNo) && (
                  <div style={{ color: "#4d5765" }}>登録番号：{displayInvoiceNo(d.invoiceNo)}</div>
                )}
                {rcp.usedOrg && <div style={{ color: "#4d5765" }}>氏名：{d.name}　様</div>}
                <div style={{ color: "#4d5765" }}>支店／業者：{d.affiliation}</div>
                <div style={{ color: "#4d5765" }}>対象月：{d.month}</div>
              </>
            );
          })()}
        </div>
        <div style={{ textAlign: "right" }}>
          <img src={logo} alt="HS-deliverys" style={{ height: 17, marginBottom: 4 }} />
          <div style={{ fontWeight: 800 }}>{company.name}</div>
          <div style={{ color: "#4d5765" }}>{company.zip} {company.addr}</div>
          <div style={{ color: "#4d5765" }}>TEL {company.tel}</div>
          <div style={{ marginTop: 3, fontWeight: 700 }}>登録番号 {company.invoiceNo}</div>
        </div>
      </div>

      <div style={{ marginTop: 16, fontWeight: 800, fontSize: 12, borderLeft: "3px solid #2563eb", paddingLeft: 7 }}>売上明細</div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 10.5 }}>
        <thead><tr>
          <th style={nth}>区分</th><th style={{ ...nth, textAlign: "right" }}>実績</th><th style={{ ...nth, textAlign: "right" }}>金額</th>
        </tr></thead>
        <tbody>
          {sales.map((sv, i) => (
            <tr key={i}>
              <td style={ntd}>
                {sv.name}{sv.reduced ? " ※" : ""}
                {sv.detail && <div style={{ fontSize: 9, color: "#828b99", marginTop: 1 }}>{sv.detail}</div>}
              </td>
              <td style={{ ...ntd, textAlign: "right" }}>{sv.qty}</td>
              <td style={{ ...ntd, textAlign: "right" }}>{group(sv.amount)}</td>
            </tr>
          ))}
          <tr style={{ background: "#f4f6f9", fontWeight: 800 }}><td style={ntd} colSpan={2}>売上合計</td><td style={{ ...ntd, textAlign: "right" }}>¥{group(t.sales)}</td></tr>
        </tbody>
      </table>

      <div style={{ marginTop: 14, fontWeight: 800, fontSize: 12, borderLeft: "3px solid #cc2f2a", paddingLeft: 7 }}>控除明細</div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 10.5 }}>
        <tbody>
          {d.deductions.map((x) => (
            <tr key={x.id}>
              <td style={ntd}>
                {x.label}{x.reduced ? " ※" : ""}
                {x.detail && <div style={{ fontSize: 9, color: "#828b99", marginTop: 1 }}>{x.detail}</div>}
              </td>
              <td style={{ ...ntd, textAlign: "right", color: "#cc2f2a" }}>△{group(x.amount)}</td>
            </tr>
          ))}
          {t.invoiceDed > 0 && <tr><td style={ntd}>値引き</td><td style={{ ...ntd, textAlign: "right", color: "#cc2f2a" }}>△{group(t.invoiceDed)}</td></tr>}
          <tr style={{ background: "#f4f6f9", fontWeight: 800 }}><td style={ntd}>控除合計</td><td style={{ ...ntd, textAlign: "right", color: "#cc2f2a" }}>△{group(t.ded)}</td></tr>
        </tbody>
      </table>

      {/* 税集計（MF通知書形式。tax10=0 の旧スナップショットは従来表示） */}
      {t.tax10 > 0 ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 14, gap: 16 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 9.5 }}>
              <thead>
                <tr>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "left", color: "#4d5765" }}>税率別内訳</th>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "right", color: "#4d5765" }}>税抜金額</th>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "right", color: "#4d5765" }}>消費税額</th>
                  <th style={{ padding: "3px 8px", borderBottom: "1px solid #cdd3dc", fontWeight: 700, textAlign: "right", color: "#4d5765" }}>税込金額</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "3px 8px" }}>10%</td>
                  <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{d.taxable10 < 0 ? "△" : ""}{group(d.taxable10)}</td>
                  <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{group(t.tax10)}</td>
                  <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{group(d.taxable10 + t.tax10)}</td>
                </tr>
                {(d.taxable8 ?? 0) !== 0 && (
                  <tr>
                    <td style={{ padding: "3px 8px" }}>8%（軽減）</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{(d.taxable8 ?? 0) < 0 ? "△" : ""}{group(d.taxable8 ?? 0)}</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{group(t.tax8)}</td>
                    <td className="num" style={{ padding: "3px 8px", textAlign: "right" }}>{group((d.taxable8 ?? 0) + t.tax8)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <table style={{ borderCollapse: "collapse", fontSize: 10.5, minWidth: 200 }}>
              <tbody>
                <tr>
                  <td style={{ padding: "3px 10px", color: "#4d5765" }}>小計</td>
                  <td className="num" style={{ padding: "3px 0", textAlign: "right", fontWeight: 700 }}>{group(t.sales - t.ded)}</td>
                </tr>
                <tr>
                  <td style={{ padding: "3px 10px", color: "#4d5765" }}>消費税額合計</td>
                  <td className="num" style={{ padding: "3px 0", textAlign: "right", fontWeight: 700 }}>{group(t.taxTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 10, background: "#1f2a44", color: "#fff", borderRadius: 6, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, letterSpacing: ".06em" }}>差引支給額（税込）</span>
            <span className="num" style={{ fontSize: 22, fontWeight: 800 }}>¥{group(t.net)}</span>
          </div>
        </>
      ) : (
        <div style={{ marginTop: 16, background: "#1f2a44", color: "#fff", borderRadius: 6, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, letterSpacing: ".06em" }}>差引支給額</span>
          <span className="num" style={{ fontSize: 22, fontWeight: 800 }}>¥{group(t.net)}</span>
        </div>
      )}

      {d.bank && <div style={{ marginTop: 12, color: "#4d5765" }}>振込先：{d.bank}</div>}
      <div style={{ color: "#4d5765", marginTop: d.bank ? 0 : 12 }}>振込予定日：{d.payDate}</div>
      {((d.taxable8 ?? 0) !== 0 || d.deductions.some((x) => x.reduced) || d.rows.some((r) => r.reduced)) && (
        <div style={{ marginTop: 10, fontSize: 10.5, fontWeight: 700 }}>※は軽減税率（8％）対象</div>
      )}
      <div style={{ marginTop: 12, borderTop: "1px solid #e2e6ec", paddingTop: 8, fontSize: 10, color: "#828b99" }}>
        備考：本通知書は適格請求書等保存方式（インボイス制度）に対応しています。
      </div>
    </div>
  );
}

export function DriverApprovalScreen() {
  const d = tanaka;
  const [printMode, setPrintMode] = useState<"notice" | "sales">("notice");
  const [printTick, setPrintTick] = useState(0);
  useEffect(() => { if (printTick > 0) window.print(); }, [printTick]);
  const doPrint = (mode: "notice" | "sales") => { setPrintMode(mode); setPrintTick((x) => x + 1); };
  return (
    <div className="dapp">
      <div className="dapp-stage">
        <div className="dapp-phone">
          <ApprovalApp d={d} onPrint={doPrint} />
        </div>
      </div>
      <div className="dapp-print-root">
        {printMode === "notice" ? <NoticeSheet d={d} /> : <PrintSheet d={d} />}
      </div>
    </div>
  );
}

// 公開承認ページ（ドライバーがトークン付きURLでアクセス）から使う部品を公開
export { ApprovalApp, PrintSheet, NoticeSheet };
